import { AlertTriangle, Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputDecimal } from "@/components/ui/input-decimal";
import { Label } from "@/components/ui/label";

import { producaoApi } from "../api";
import { fmtBRL, fmtQtd } from "../lib/formato";

/** Popup "Produzir": lotes ⇄ quantidade, previa do que baixa/sobe e confirmacao.
 *  Falta de ingrediente base AVISA e deixa produzir (baixa ate zero). */
export default function DialogProduzir({ receita, onClose, onProduzido, toast }) {
  const [lotes, setLotes] = useState("1");
  const [qtd, setQtd] = useState(receita ? String(receita.rende || 0) : "");
  const [obs, setObs] = useState("");
  const [previa, setPrevia] = useState(null);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const rende = Number(receita?.rende) || 0;

  useEffect(() => {
    if (!receita) return undefined;
    const n = Number(lotes) || 0;
    if (n <= 0) { setPrevia(null); return undefined; }
    const t = setTimeout(() => {
      producaoApi.previa(receita.product_id, n).then((p) => { setPrevia(p); setErro(""); }).catch((e) => { setPrevia(null); setErro(e.message); });
    }, 250);
    return () => clearTimeout(t);
  }, [receita, lotes]);

  if (!receita) return null;
  const mudarLotes = (v) => { setLotes(v); setQtd(rende > 0 && v !== "" ? String(Math.round(Number(v) * rende * 1000) / 1000) : ""); };
  const mudarQtd = (v) => { setQtd(v); setLotes(rende > 0 && v !== "" ? String(Math.round((Number(v) / rende) * 100) / 100) : ""); };
  const confirmar = async () => {
    if (!previa || salvando) return;
    setSalvando(true);
    try {
      const r = await producaoApi.produzir({ product_id: receita.product_id, lotes: Number(lotes), observacao: obs });
      toast.success(`Produção registrada: ${fmtQtd(r.produzido, r.unidade)} ${r.unidade} de ${r.name}.`);
      onProduzido(r); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSalvando(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-e2e="dialog-produzir">
        <DialogHeader><DialogTitle>Produzir: {receita.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="prod-lotes" className="text-xs">Lotes</Label>
              <InputDecimal id="prod-lotes" decimals={2} value={lotes} onChange={mudarLotes} className="mt-1 h-10" />
              <p className="text-[11px] text-muted-foreground mt-1">1 lote = {fmtQtd(rende, receita.unidade)} {receita.unidade}</p>
            </div>
            <div>
              <Label htmlFor="prod-qtd" className="text-xs">Quantidade produzida</Label>
              <InputDecimal id="prod-qtd" decimals={3} sufixo={receita.unidade} value={qtd} onChange={mudarQtd} className="mt-1 h-10" />
              <p className="text-[11px] text-muted-foreground mt-1">Pode digitar aqui também; os lotes acompanham.</p>
            </div>
          </div>
          {erro && <p className="text-sm text-destructive" data-e2e="erro-previa">{erro}</p>}
          {previa && (
            <div className="rounded-xl border divide-y" data-e2e="previa">
              <div className="p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Vai baixar</p>
                <ul className="space-y-1">
                  {previa.bases.map((b) => (
                    <li key={b.ingredient_id} className="flex items-center justify-between gap-2 text-sm" data-e2e="base">
                      <span className="truncate">{b.name}</span>
                      <span className={`inline-flex items-center gap-1 tabular-nums whitespace-nowrap ${b.falta > 0 ? "text-amber-600 dark:text-amber-400" : ""}`}>
                        {fmtQtd(b.precisa, b.unit)} {b.unit} <span className="text-muted-foreground">(tem {fmtQtd(b.tem, b.unit)})</span>
                        {b.falta > 0 ? <AlertTriangle className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5 text-emerald-500" />}
                      </span>
                    </li>
                  ))}
                </ul>
                {previa.faltando.length > 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2" data-e2e="aviso-falta">
                    Falta estoque de {previa.faltando.join(", ")}. Pode produzir mesmo assim: o saldo desses itens vai a zero (nunca fica negativo).
                  </p>
                )}
              </div>
              <div className="p-3 flex items-center justify-between text-sm">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Vai subir</span>
                <span className="tabular-nums" data-e2e="sobe"><strong>{receita.pronto_nome}</strong> +{fmtQtd(previa.produzido, previa.unidade)} {previa.unidade} → fica {fmtQtd(previa.estoque_depois, previa.unidade)} {previa.unidade}</span>
              </div>
              <div className="p-3 flex items-center justify-between text-sm">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Custo desta produção</span>
                <span className="font-bold tabular-nums" data-e2e="custo">{fmtBRL(previa.custo)}</span>
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="prod-obs" className="text-xs">Observação (opcional)</Label>
            <input id="prod-obs" value={obs} onChange={(e) => setObs(e.target.value)} maxLength={500} className="mt-1 w-full h-10 rounded-md border bg-background px-3 text-sm" placeholder="Ex.: lote da manhã" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={confirmar} disabled={!previa || salvando} data-e2e="confirmar-producao">
              {salvando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Confirmar produção
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
